<!-- resources/js/components/UserActivityReport.vue -->
<template>
  <div>

    <!-- Cartes de statistiques -->
    <b-row>
      <b-col lg="4" sm="6">
        <b-card>
          <div class="d-flex justify-content-between">
            <div>
              <h2 class="font-weight-bolder mb-0">{{ stats.total_users }}</h2>
              <p class="card-text">Total Utilisateurs</p>
            </div>
            <div class="avatar avatar-stats p-50 m-0 bg-light-primary">
              <div class="avatar-content">
                <feather-icon icon="UsersIcon" />
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col lg="4" sm="6">
        <b-card>
          <div class="d-flex justify-content-between">
            <div>
              <h2 class="font-weight-bolder mb-0 text-success">{{ stats.active_users }}</h2>
              <p class="card-text">Utilisateurs Actifs</p>
              <b-badge variant="success">{{ activePercentage }}%</b-badge>
            </div>
            <div class="avatar avatar-stats p-50 m-0 bg-light-success">
              <div class="avatar-content">
                <feather-icon icon="UserCheckIcon" />
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col lg="4" sm="6">
        <b-card>
          <div class="d-flex justify-content-between">
            <div>
              <h2 class="font-weight-bolder mb-0 text-warning">{{ stats.inactive_users }}</h2>
              <p class="card-text">Utilisateurs Inactifs</p>
              <b-badge variant="warning">{{ inactivePercentage }}%</b-badge>
            </div>
            <div class="avatar avatar-stats p-50 m-0 bg-light-warning">
              <div class="avatar-content">
                <feather-icon icon="UserXIcon" />
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col lg="3" v-if="false" sm="6">
        <b-card>
          <div class="d-flex justify-content-between">
            <div>
              <h2 class="font-weight-bolder mb-0">{{ stats.avg_duration }}</h2>
              <p class="card-text">Temps Moyen</p>
            </div>
            <div class="avatar avatar-stats p-50 m-0 bg-light-info">
              <div class="avatar-content">
                <feather-icon icon="ClockIcon" />
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Tableau des utilisateurs -->
    <b-row>
      <b-col>
        <b-overlay :show="is_loading">
          <b-card no-body>
            <!-- Tabs pour actifs/inactifs -->
            <b-tabs card>
              <b-tab :title="`Utilisateurs Actifs (${activeUsersFiltered.length})`" active>
                <div class="m-2 d-flex justify-content-between">
                  <div class="align-self-center font-weight-bold">
                    {{ activeUsersFiltered.length }} utilisateurs actifs cette période
                  </div>
                  <div>
                    <b-button
                        size="sm"
                        variant="outline-primary"
                        @click="refreshData"
                    >
                      <feather-icon icon="RefreshCwIcon" />
                      Actualiser
                    </b-button>
                  </div>
                </div>

                <!-- Table utilisateurs actifs -->
                <b-table
                    v-if="activeUsersFiltered.length"
                    :fields="activeFields"
                    :items="activeUsersFiltered"
                    responsive
                    show-empty
                    :busy="is_loading"
                    striped
                    hover
                    class="mb-0"
                >
                  <template #cell(user)="data">
                    <div class="d-flex align-items-center">
                      <b-avatar
                          :text="getInitials(data.item.name)"
                          class="mr-2"
                          size="32"
                          variant="light-primary"
                      />
                      <div>
                        <div class="font-weight-bold">{{ data.item.name }}</div>
                        <div class="text-muted font-small-2">{{ data.item.email }}</div>
                      </div>
                    </div>
                  </template>

                  <template #cell(connections)="data">
                    <div class="text-center">
                      <div class="font-weight-bold">{{ data.item.connections }}</div>
                      <small class="text-muted">connexions</small>
                    </div>
                  </template>

                  <template #cell(total_time)="data">
                    <b-badge
                        :variant="getTimeBadgeVariant(data.item.total_duration_seconds)"
                    >
                      {{ data.item.total_time }}
                    </b-badge>
                  </template>

                  <template #cell(avg_time)="data">
                    <div>{{ data.item.avg_time }}</div>
                  </template>

                  <template #cell(last_activity)="data">
                    <div class="font-small-3">
                      <div>Première: {{ formatDateTime(data.item.first_login) }}</div>
                      <div>Dernière: {{ formatDateTime(data.item.last_login) }}</div>
                    </div>
                  </template>

                  <template #cell(status)="data">
                    <b-badge variant="success">Actif</b-badge>
                  </template>

                  <template #cell(actions)="data">
                    <b-button
                      v-if="$can('consulter-journal-actions', 'parametres')"
                      :to="{ name: 'journal-utilisateur', params: { id: data.item.user_id } }"
                      v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                      size="sm"
                      variant="outline-info"
                    >
                      <feather-icon icon="ListIcon" size="14" />
                      Journal
                    </b-button>
                  </template>

                </b-table>

                <div v-else class="text-center p-4">
                  <feather-icon icon="UsersIcon" size="48" class="mb-2 text-muted" />
                  <p class="text-muted">Aucun utilisateur actif pour cette période</p>
                </div>
              </b-tab>

              <b-tab :title="`Utilisateurs Inactifs (${inactiveUsersFiltered.length})`">
                <div class="m-2 d-flex justify-content-between">
                  <div class="align-self-center font-weight-bold text-warning">
                    {{ inactiveUsersFiltered.length }} utilisateurs inactifs cette période
                  </div>
                </div>

                <!-- Table utilisateurs inactifs -->
                <b-table
                    v-if="inactiveUsersFiltered.length"
                    :fields="inactiveFields"
                    :items="inactiveUsersFiltered"
                    responsive
                    show-empty
                    striped
                    hover
                    class="mb-0"
                >
                  <template #cell(user)="data">
                    <div class="d-flex align-items-center">
                      <b-avatar
                          :text="getInitials(data.item.name)"
                          class="mr-2"
                          size="32"
                          variant="light-warning"
                      />
                      <div>
                        <div class="font-weight-bold">{{ data.item.name }}</div>
                        <div class="text-muted font-small-2">{{ data.item.email }}</div>
                      </div>
                    </div>
                  </template>

                  <template #cell(last_known_login)="data">
                    <div>
                      <b-badge
                          :variant="data.item.last_known_login === 'Jamais connecté' ? 'danger' : 'warning'"
                      >
                        {{ data.item.last_known_login }}
                      </b-badge>
                    </div>
                  </template>

                  <template #cell(status)="data">
                    <b-badge variant="warning">Inactif</b-badge>
                  </template>

                  <template #cell(actions)="data">
                    <div class="d-flex flex-nowrap" style="gap: 0.5rem;">
                      <b-button
                          variant="flat-primary"
                          size="sm"
                          @click="sendActivationEmail(data.item)"
                      >
                        <feather-icon icon="SendIcon" size="14" />
                        Relancer
                      </b-button>
                      <b-button
                        v-if="$can('consulter-journal-actions', 'parametres')"
                        :to="{ name: 'journal-utilisateur', params: { id: data.item.user_id } }"
                        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                        size="sm"
                        variant="outline-info"
                      >
                        <feather-icon icon="ListIcon" size="14" />
                        Journal
                      </b-button>
                    </div>
                  </template>
                </b-table>

                <div v-else class="text-center p-4">
                  <feather-icon icon="UserXIcon" size="48" class="mb-2 text-muted" />
                  <p class="text-muted">Aucun utilisateur inactif pour cette période</p>
                </div>
              </b-tab>


            </b-tabs>
          </b-card>
        </b-overlay>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BForm,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormDatepicker,
  BButton,
  BTable,
  BBadge,
  BAvatar,
  BOverlay,
  BSpinner,
  BTabs,
  BTab,
  BDropdown,
  BDropdownItem
} from 'bootstrap-vue'
import axios from '@/libs/axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Ripple from 'vue-ripple-directive'
import filterPersistence from '@/mixins/filterPersistence'

export default {
  name: 'UserActivityReport',
  mixins: [filterPersistence],

  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BFormDatepicker,
    BButton,
    BTable,
    BBadge,
    BAvatar,
    BOverlay,
    BSpinner,
    BTabs,
    BTab,
    BDropdown,
    BDropdownItem
  },

  directives: {
    Ripple
  },

  data() {
    return {
      filterKey: 'user-activity-report',
      is_loading: false,
      pendingExport: false,
      pendingSend: false,

      filter: {
        type: 'week',
        week: this.getCurrentWeekStart(),
        startDate: null,
        endDate: null,
        search: ''
      },

      stats: {
        total_users: 0,
        active_users: 0,
        inactive_users: 0,
        avg_duration: '0 min'
      },

      activeUsers: [],
      inactiveUsers: [],

      activeFields: [
        {
          key: 'user',
          label: 'Utilisateur',
          sortable: true
        },
        {
          key: 'connections',
          label: 'Connexions',
          sortable: true,
          class: 'text-center'
        },
        // {
        //   key: 'total_time',
        //   label: 'Temps Total',
        //   sortable: true
        // },
        // {
        //   key: 'avg_time',
        //   label: 'Temps Moyen',
        //   sortable: true
        // },
        {
          key: 'last_activity',
          label: 'Activité'
        },
        {
          key: 'status',
          label: 'Statut',
          class: 'text-center'
        },
        {
          key: 'actions',
          label: 'Actions'
        },

      ],

      inactiveFields: [
        {
          key: 'user',
          label: 'Utilisateur',
          sortable: true
        },
        {
          key: 'last_known_login',
          label: 'Dernière connexion',
          sortable: true
        },
        {
          key: 'status',
          label: 'Statut',
          class: 'text-center'
        },
        {
          key: 'actions',
          label: 'Actions'
        }
      ],

      availableWeeks: []
    }
  },

  computed: {
    activePercentage() {
      if (!this.stats.total_users) return 0
      return Math.round((this.stats.active_users / this.stats.total_users) * 100)
    },

    inactivePercentage() {
      if (!this.stats.total_users) return 0
      return Math.round((this.stats.inactive_users / this.stats.total_users) * 100)
    },

    activeUsersFiltered() {
      if (!this.filter.search) return this.activeUsers

      const search = this.filter.search.toLowerCase()
      return this.activeUsers.filter(user =>
          user.name.toLowerCase().includes(search) ||
          user.email.toLowerCase().includes(search)
      )
    },

    inactiveUsersFiltered() {
      if (!this.filter.search) return this.inactiveUsers

      const search = this.filter.search.toLowerCase()
      return this.inactiveUsers.filter(user =>
          user.name.toLowerCase().includes(search) ||
          user.email.toLowerCase().includes(search)
      )
    }
  },

  mounted() {
    this.generateWeekOptions()
    this.loadReport()
  },

  methods: {
    getCurrentWeekStart() {
      const now = new Date()
      const monday = new Date(now)
      monday.setDate(monday.getDate() - (monday.getDay() || 7) + 1)
      return monday.toISOString().split('T')[0]
    },

    generateWeekOptions() {
      const options = []
      for (let i = 0; i < 8; i++) {
        const weekStart = new Date()
        weekStart.setDate(weekStart.getDate() - (weekStart.getDay() || 7) + 1 - (i * 7))
        const weekEnd = new Date(weekStart)
        weekEnd.setDate(weekEnd.getDate() + 6)

        options.push({
          value: weekStart.toISOString().split('T')[0],
          label: i === 0
              ? 'Semaine actuelle'
              : `${weekStart.toLocaleDateString('fr')} - ${weekEnd.toLocaleDateString('fr')}`
        })
      }
      this.availableWeeks = options
    },

    async loadReport() {
      this.is_loading = true

      try {
        let params = {}

        if (this.filter.type === 'week') {
          params.week_start = this.filter.week
        } else if (this.filter.type === 'custom') {
          params.start_date = this.filter.startDate
          params.end_date = this.filter.endDate
        }

        const response = await axios.get('/api/analytics/user-activity', { params })

        this.stats = {
          total_users: response.data.total_stellantis_users,
          active_users: response.data.active_users_count,
          inactive_users: response.data.inactive_users_count,
          avg_duration: response.data.platform_stats?.avg_session_duration || '0 min'
        }

        this.activeUsers = response.data.active_users || []
        this.inactiveUsers = response.data.inactive_users || []

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Rapport chargé',
            text: 'Les données ont été actualisées',
            variant: 'success',
            icon: 'CheckIcon'
          }
        })

      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            text: 'Impossible de charger le rapport',
            variant: 'danger',
            icon: 'XIcon'
          }
        })
      } finally {
        this.is_loading = false
      }
    },

    async exportReport() {
      this.pendingExport = true

      try {
        let params = { export: 'excel' }

        if (this.filter.type === 'week') {
          params.week_start = this.filter.week
        }

        const response = await axios.get('/api/analytics/user-activity', {
          params,
          responseType: 'blob'
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `rapport_africavo_stellantis_${this.filter.week}.xlsx`)
        document.body.appendChild(link)
        link.click()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Export réussi',
            text: 'Le fichier Excel a été téléchargé',
            variant: 'success',
            icon: 'DownloadIcon'
          }
        })

      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            text: 'Impossible d\'exporter le rapport',
            variant: 'danger',
            icon: 'XIcon'
          }
        })
      } finally {
        this.pendingExport = false
      }
    },

    async sendReport() {
      this.pendingSend = true

      try {
        await axios.post('/api/user-activity/send-report', {
          week_start: this.filter.week
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Envoi réussi',
            text: 'Le rapport a été envoyé à Stellantis',
            variant: 'success',
            icon: 'SendIcon'
          }
        })

      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            text: 'Impossible d\'envoyer le rapport',
            variant: 'danger',
            icon: 'XIcon'
          }
        })
      } finally {
        this.pendingSend = false
      }
    },

    getInitials(name) {
      if (!name) return '?'
      return name.split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
          .substring(0, 2)
    },

    getTimeBadgeVariant(seconds) {
      const hours = seconds / 3600
      if (hours > 4) return 'success'
      if (hours > 1) return 'warning'
      return 'danger'
    },

    formatDateTime(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr') + ' ' + date.toLocaleTimeString('fr', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    refreshData() {
      this.loadReport()
    },

    filterUsers() {
      // Le filtrage est géré par les computed properties
    },

    viewUserDetails(user) {
      this.$router.push({
        name: 'user-activity-details',
        params: { userId: user.user_id }
      })
    },

    async exportUserReport(user) {
      try {
        const response = await axios.get(`/api/user-activity/user/${user.user_id}/export`, {
          responseType: 'blob'
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `rapport_${user.email}.xlsx`)
        document.body.appendChild(link)
        link.click()

      } catch (error) {
        console.error('Erreur export utilisateur:', error)
      }
    },

    async sendReminder(user) {
      try {
        await axios.post(`/api/user-activity/reminder`, {
          user_id: user.user_id
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Rappel envoyé',
            text: `Un email a été envoyé à ${user.email}`,
            variant: 'success',
            icon: 'MailIcon'
          }
        })

      } catch (error) {
        console.error('Erreur envoi rappel:', error)
      }
    },

    async sendActivationEmail(user) {
      try {
        await axios.post(`/api/user-activity/activation`, {
          user_id: user.user_id
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Email d\'activation envoyé',
            text: `Un email a été envoyé à ${user.email}`,
            variant: 'success',
            icon: 'MailIcon'
          }
        })

      } catch (error) {
        console.error('Erreur envoi activation:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.avatar-stats {
  height: 56px;
  width: 56px;

  .avatar-content {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
}

.font-small-2 {
  font-size: 0.875rem;
}

.font-small-3 {
  font-size: 0.75rem;
}
</style>
